import React, {useMemo} from 'react';
import {withStyles} from '@mui/styles';
import {avgColors, getColorRGB, gray, toRGBA} from "./colorUtils";

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    margin: "0",
    height: "100%",
    flex: 1,
  },
  colorRow: {
    flex: 1,
    display: "flex",
  },
  gap1px: {
    gap: "1px",
  },
  colorCellWrap: {
    flex: 1,
    display: "flex",
    backgroundColor: "rgb(255, 255, 255, 0.75)",
    alignItems: "center",
    margin: "-1px",
    padding: "1px",
  },
  colorCell: {
    flex: 1,
    transition: "background-color 0.25s",
    transformOrigin: "center",
    textAlign: "center",
    verticalAlign: "middle",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxHeight: "1.75rem",
    height: "100%",
    borderRadius: '2px',
  },
  colorCellFixed: {
    width: "1.75rem",
    height: "1.75rem",
    fontSize: "0.7225em",
    border: `1px solid ${toRGBA(gray(1), 1)}`,
    borderRadius: '4px',
    margin: "1px",
  },
  colorCellSquare: {
    borderRadius: '0px',
  },
}

const calcLogValue = (answerCount, answerMax, defVal, mult, add) => answerCount === 0 ? defVal
  : (Math.log(Math.max(1, answerCount+1)) / Math.log(Math.max(1, answerMax+1)) * mult) + add;
const calcAnswersAlpha = (count, max) => calcLogValue(count, max, 0.063, 0.825, 0.03125);
const calcAnswersScale = (count, max) => calcLogValue(count, max, 0.35, 0.5, 0.5);

const bound = (val, max, min=0) => Math.max(min, Math.min(val, max));

function ColorGrid(props) {
  const {surveyAttempt, className, fixedWidth=false, noGap=false, classes} = props;
  const xChoices = surveyAttempt.survey.options.choices[0];
  const yChoices = surveyAttempt.survey.options.choices[1] || {};
  const xCount = xChoices.answers?.length || 1;
  const yCount = yChoices?.answers?.length || 1;

  const qData = useMemo(() => {
    const qData = []
    for (let i = 0; i < yCount; i++) {
      const r = [];
      for (let l = 0; l < xCount; l++) {
        r.push(0);
      }
      qData.push(r);
    }
    surveyAttempt.answers.forEach(([id, [xVal, yVal] = []], i) => {
      if (typeof(xVal) === "number" && typeof(yVal) === "number") {
        qData[bound(yVal-1, yCount-1)][bound(xVal-1, xCount-1)] = qData[bound(yVal-1, yCount-1)][bound(xVal-1, xCount-1)] + 1;
      } else if (typeof(xVal) === "number") {
        qData[0][bound(xVal-1, xCount-1)] = qData[0][bound(xVal-1, xCount-1)] + 1;
      }
    })
    return qData;
  }, [surveyAttempt.answers, xCount, yCount]);

  const answersMax = useMemo(() => {
    let maxVal = 0;
    qData.forEach(row => row.forEach(cell => maxVal = Math.max(maxVal, cell)));
    return maxVal;
  }, [qData]);

  return (
    <div className={`${classes.root} ${!noGap ? classes.gap1px : ""} ${className||""}` }>
      {new Array(yCount).fill(0).map((_, k) => {
        const i = (yCount-1)-k;
        const impactCol = yChoices?.color_class ? getColorRGB(yChoices?.color_class, i, yCount) : null;
        return <div key={`disp_${i}`} className={!fixedWidth && noGap ? classes.colorRow : `${classes.colorRow} ${classes.gap1px}`}>
          {new Array(xCount).fill(0).map((_, l) => {
            const likelyCol = getColorRGB(xChoices.color_class, l, xCount);
            const color = !!impactCol ? avgColors(impactCol, likelyCol) : likelyCol;
            const alpha = calcAnswersAlpha(qData[i][l], answersMax);
            const scale = calcAnswersScale(qData[i][l], answersMax);
            return (
              <div key={`disp_${i}_${l}`} className={classes.colorCellWrap}>
                <div className={!fixedWidth ? !noGap ? classes.colorCell : `${classes.colorCell} ${classes.colorCellSquare}` : `${classes.colorCell} ${classes.colorCellFixed}`} style={{
                    backgroundColor: `rgba(${color[0]},${color[1]},${color[2]},${alpha})`,
                    transform: `scale(${scale})`,
                  }}
                >{!fixedWidth ? null : qData[i][l] > 0 ? qData[i][l] : null}</div>
            </div>);
          })}
        </div>;
      })}
    </div>
  );
}

export default withStyles(styles)(ColorGrid);